import { ButtonNavButton } from '@front-app/components/buttons/buttonNav';
import { DataTableColumn, SortDirection } from '@front-app/components/dataDashboard/model';
import { DataPointName, DateData } from '@front-app/hooks/DataDashboard/model';
import { GroupDataRange, GroupDataType } from '@front-app/services/GroupsApi/GroupDataModel';
import {
	FontAwesomeIconProps,
	DISCUSSIONS_ICON,
	DISCUSSION_VIEW_ICON,
	EVENT_MEMBERS_ICON,
	EVENT_VIEW_ICON,
	GROUP_VIEW_ICON,
	MEMBERS_ICON,
	SHARES_ICON,
	EVENT_CREATION_ICON,
	DOWNLOAD_ICON_LARGE,
} from '@front-app/vendor/icon-definitions';

export const DATA_SOURCE_ICONS: Record<GroupDataType, FontAwesomeIconProps> = {
	[GroupDataType.MEMBERS]: MEMBERS_ICON,
	[GroupDataType.SHARES]: SHARES_ICON,
	[GroupDataType.DISCUSSION_TOPICS]: DISCUSSIONS_ICON,
	[GroupDataType.DISCUSSION_VIEWS]: DISCUSSION_VIEW_ICON,
	[GroupDataType.DISCUSSION_POSTS]: DISCUSSIONS_ICON,
	[GroupDataType.GROUP_VIEWS]: GROUP_VIEW_ICON,
	[GroupDataType.EVENT_VIEWS]: EVENT_VIEW_ICON,
	[GroupDataType.EVENT_MEMBERS]: EVENT_MEMBERS_ICON,
	[GroupDataType.EVENT_CREATIONS]: EVENT_CREATION_ICON,
	[GroupDataType.FILE_DOWNLOADS]: DOWNLOAD_ICON_LARGE,
};

export const DATA_SOURCE_LABELS: Record<GroupDataType, string> = {
	[GroupDataType.MEMBERS]: 'data-source.members',
	[GroupDataType.SHARES]: 'data-source.shares',
	[GroupDataType.DISCUSSION_TOPICS]: 'data-source.discussion-topics',
	[GroupDataType.DISCUSSION_VIEWS]: 'data-source.discussion-views',
	[GroupDataType.DISCUSSION_POSTS]: 'data-source.discussion-posts',
	[GroupDataType.GROUP_VIEWS]: 'data-source.group-views',
	[GroupDataType.EVENT_VIEWS]: 'data-source.event-views',
	[GroupDataType.EVENT_MEMBERS]: 'data-source.event-members',
	[GroupDataType.EVENT_CREATIONS]: 'data-source.event-creations',
	[GroupDataType.FILE_DOWNLOADS]: 'data-source.file-downloads',
};

export const DATA_SOURCE_BUTTON_LIST: ButtonNavButton[] = [
	GroupDataType.MEMBERS,
	GroupDataType.SHARES,
	GroupDataType.DISCUSSION_TOPICS,
	GroupDataType.DISCUSSION_VIEWS,
	GroupDataType.DISCUSSION_POSTS,
	GroupDataType.GROUP_VIEWS,
	GroupDataType.EVENT_VIEWS,
	GroupDataType.EVENT_MEMBERS,
	GroupDataType.EVENT_CREATIONS,
	GroupDataType.FILE_DOWNLOADS,
].map((key) => ({
	key,
	label: DATA_SOURCE_LABELS[key],
	to: {},
}));

export const DATA_RANGE_LABELS: Record<GroupDataRange, string> = {
	[GroupDataRange.LAST_30_DAYS]: 'data-range.last-30-days',
	[GroupDataRange.LAST_90_DAYS]: 'data-range.last-90-days',
	[GroupDataRange.LAST_1_YEAR]: 'data-range.last-year',
};

export const DATA_RANGE_BUTTON_LIST: ButtonNavButton[] = [
	{
		key: GroupDataRange.LAST_30_DAYS,
		label: DATA_RANGE_LABELS[GroupDataRange.LAST_30_DAYS],
		to: {},
	},
	{
		key: GroupDataRange.LAST_90_DAYS,
		label: DATA_RANGE_LABELS[GroupDataRange.LAST_90_DAYS],
		to: {},
	},
	{
		key: GroupDataRange.LAST_1_YEAR,
		label: DATA_RANGE_LABELS[GroupDataRange.LAST_1_YEAR],
		to: {},
	},
];

export const DATA_TABLE_COLUMNS: Record<GroupDataType, DataTableColumn> = {
	[GroupDataType.MEMBERS]: {
		dateTitle: 'data-dashboard.columns.members.date',
		totalTitle: 'data-dashboard.columns.members.total-members',
		windowedAverageTitle: 'data-dashboard.columns.window-average',
		dataPointTitles: [
			{
				dataPoint: DataPointName.MEMBER_JOINS,
				title: 'data-dashboard.columns.members.joins',
			},
			{
				dataPoint: DataPointName.MEMBER_LEAVES,
				title: 'data-dashboard.columns.members.leaves',
			},
		],
	},
	[GroupDataType.SHARES]: {
		dateTitle: 'data-dashboard.columns.shares.date',
		totalTitle: 'data-dashboard.columns.shares.total-shares',
		windowedAverageTitle: 'data-dashboard.columns.window-average',
		dataPointTitles: [
			{
				dataPoint: DataPointName.SHARE_UPLOADS,
				title: 'data-dashboard.columns.shares.uploads',
			},
			{
				dataPoint: DataPointName.SHARE_REMOVES,
				title: 'data-dashboard.columns.shares.removes',
			},
		],
	},
	[GroupDataType.DISCUSSION_TOPICS]: {
		dateTitle: 'data-dashboard.columns.discussionTopics.date',
		totalTitle: 'data-dashboard.columns.discussionTopics.total-topics',
		windowedAverageTitle: 'data-dashboard.columns.window-average',
		dataPointTitles: [
			{
				dataPoint: DataPointName.CREATE_DISCUSSION,
				title: 'data-dashboard.columns.discussionTopics.new-topics',
			},
		],
	},
	[GroupDataType.DISCUSSION_POSTS]: {
		dateTitle: 'data-dashboard.columns.discussionTopics.date',
		totalTitle: 'data-dashboard.columns.views.total-posts',
		windowedAverageTitle: 'data-dashboard.columns.window-average',
		dataPointTitles: [
			{
				dataPoint: DataPointName.READ_DISCUSSION,
				title: 'data-dashboard.columns.posts.posts',
			},
		],
	},
	[GroupDataType.DISCUSSION_VIEWS]: {
		dateTitle: 'data-dashboard.columns.discussionTopics.date',
		totalTitle: 'data-dashboard.columns.views.total-views',
		windowedAverageTitle: 'data-dashboard.columns.window-average',
		dataPointTitles: [
			{
				dataPoint: DataPointName.READ_DISCUSSION,
				title: 'data-dashboard.columns.views.views',
			},
		],
	},
	[GroupDataType.GROUP_VIEWS]: {
		dateTitle: 'data-dashboard.columns.discussionTopics.date',
		totalTitle: 'data-dashboard.columns.views.total-views',
		windowedAverageTitle: 'data-dashboard.columns.window-average',
		dataPointTitles: [
			{
				dataPoint: DataPointName.VIEW,
				title: 'data-dashboard.columns.views.views',
			},
		],
	},
	[GroupDataType.EVENT_VIEWS]: {
		dateTitle: 'data-dashboard.columns.discussionTopics.date',
		totalTitle: 'data-dashboard.columns.views.total-views',
		windowedAverageTitle: 'data-dashboard.columns.window-average',
		dataPointTitles: [
			{
				dataPoint: DataPointName.VIEW,
				title: 'data-dashboard.columns.views.views',
			},
		],
	},
	[GroupDataType.EVENT_MEMBERS]: {
		dateTitle: 'data-dashboard.columns.members.date',
		totalTitle: 'data-dashboard.columns.members.total-members',
		windowedAverageTitle: 'data-dashboard.columns.window-average',
		dataPointTitles: [
			{
				dataPoint: DataPointName.MEMBER_JOINS,
				title: 'data-dashboard.columns.members.joins',
			},
			{
				dataPoint: DataPointName.MEMBER_LEAVES,
				title: 'data-dashboard.columns.members.leaves',
			},
		],
	},
	[GroupDataType.EVENT_CREATIONS]: {
		dateTitle: 'data-dashboard.columns.members.date',
		totalTitle: 'data-dashboard.columns.event-creation.total-events-created',
		windowedAverageTitle: 'data-dashboard.columns.window-average',
		dataPointTitles: [
			{
				dataPoint: DataPointName.CREATE_EVENT,
				title: 'data-dashboard.columns.event-creation.events-created',
			},
		],
	},
	[GroupDataType.FILE_DOWNLOADS]: {
		dateTitle: 'data-dashboard.columns.members.date',
		totalTitle: 'data-dashboard.columns.file-downloads.total-file-downloads',
		windowedAverageTitle: 'data-dashboard.columns.file-downloads.average-file-downloads',
		dataPointTitles: [
			{
				dataPoint: DataPointName.FILE_DOWNLOADS,
				title: 'data-dashboard.columns.file-downloads.files-downloaded',
			},
		],
	},
};

type DataSorter = (a: DateData, b: DateData) => number;

function sortByDateDesc(a: DateData, b: DateData): number {
	return b.date.getTime() - a.date.getTime();
}

function sortByDateAsc(a: DateData, b: DateData): number {
	return a.date.getTime() - b.date.getTime();
}

function sortByTotalDesc(a: DateData, b: DateData): number {
	return b.totalAtDate - a.totalAtDate;
}

function sortByTotalAsc(a: DateData, b: DateData): number {
	return a.totalAtDate - b.totalAtDate;
}

function sortByWindowedAverageDesc(a: DateData, b: DateData): number {
	return b.windowedAverageAtDate - a.windowedAverageAtDate;
}

function sortByWindowedAverageAsc(a: DateData, b: DateData): number {
	return a.windowedAverageAtDate - b.windowedAverageAtDate;
}

const makeInnerSorter = (key: DataPointName, direction: SortDirection): DataSorter => {
	if (direction === SortDirection.ASC) {
		return (a: DateData, b: DateData) => a.data[key] - b.data[key];
	}
	return (a: DateData, b: DateData) => b.data[key] - a.data[key];
};

export const DEFAULT_SORT = sortByDateDesc;

type DirectionalSort = Record<SortDirection, DataSorter>;

export const ARIA_LABELS: Record<DataPointName, Record<SortDirection, string>> = {
	[DataPointName.DATE]: {
		[SortDirection.ASC]: 'data-dashboard.sort.a11y.date-descending',
		[SortDirection.DESC]: 'data-dashboard.sort.a11y.date-ascending',
	},
	[DataPointName.CUMULATIVE_TOTAL]: {
		[SortDirection.ASC]: 'data-dashboard.sort.a11y.cumulative-total-ascending',
		[SortDirection.DESC]: 'data-dashboard.sort.a11y.cumulative-total-descending',
	},
	[DataPointName.MEMBER_JOINS]: {
		[SortDirection.ASC]: 'data-dashboard.sort.a11y.member-joins-ascending',
		[SortDirection.DESC]: 'data-dashboard.sort.a11y.member-joins-descending',
	},
	[DataPointName.MEMBER_LEAVES]: {
		[SortDirection.ASC]: 'data-dashboard.sort.a11y.member-leaves-ascending',
		[SortDirection.DESC]: 'data-dashboard.sort.a11y.member-leaves-descending',
	},
	[DataPointName.SHARE_UPLOADS]: {
		[SortDirection.ASC]: 'data-dashboard.sort.a11y.shares-ascending',
		[SortDirection.DESC]: 'data-dashboard.sort.a11y.shares-descending',
	},
	[DataPointName.SHARE_REMOVES]: {
		[SortDirection.ASC]: 'data-dashboard.sort.a11y.removed-shares-ascending',
		[SortDirection.DESC]: 'data-dashboard.sort.a11y.removed-shares-descending',
	},
	[DataPointName.CREATE_DISCUSSION]: {
		[SortDirection.ASC]: 'data-dashboard.sort.a11y.new-discussion-topics-ascending',
		[SortDirection.DESC]: 'data-dashboard.sort.a11y.new-discussion-topics-descending',
	},
	[DataPointName.DISCUSSION_POSTS]: {
		[SortDirection.ASC]: 'data-dashboard.sort.a11y.discussion-posts-ascending',
		[SortDirection.DESC]: 'data-dashboard.sort.a11y.discussion-posts-descending',
	},
	[DataPointName.READ_DISCUSSION]: {
		[SortDirection.ASC]: 'data-dashboard.sort.a11y.views-ascending',
		[SortDirection.DESC]: 'data-dashboard.sort.a11y.views-descending',
	},
	[DataPointName.VIEW]: {
		[SortDirection.ASC]: 'data-dashboard.sort.a11y.views-ascending',
		[SortDirection.DESC]: 'data-dashboard.sort.a11y.views-descending',
	},
	[DataPointName.FILE_DOWNLOADS]: {
		[SortDirection.ASC]: 'data-dashboard.sort.a11y.file-downloads-ascending',
		[SortDirection.DESC]: 'data-dashboard.sort.a11y.file-downloads-descending',
	},
	[DataPointName.WINDOWED_AVERAGE]: {
		[SortDirection.ASC]: 'data-dashboard.sort.a11y.window-average-ascending',
		[SortDirection.DESC]: 'data-dashboard.sort.a11y.window-average-descending',
	},
	[DataPointName.CREATE_EVENT]: {
		[SortDirection.ASC]: 'data-dashboard.sort.a11y.window-average-ascending',
		[SortDirection.DESC]: 'data-dashboard.sort.a11y.window-average-descending',
	},
};

export const SORT_MAP: Record<DataPointName, DirectionalSort> = {
	[DataPointName.DATE]: {
		[SortDirection.ASC]: sortByDateAsc,
		[SortDirection.DESC]: sortByDateDesc,
	},
	[DataPointName.CUMULATIVE_TOTAL]: {
		[SortDirection.ASC]: sortByTotalAsc,
		[SortDirection.DESC]: sortByTotalDesc,
	},
	[DataPointName.MEMBER_JOINS]: {
		[SortDirection.ASC]: makeInnerSorter(DataPointName.MEMBER_JOINS, SortDirection.ASC),
		[SortDirection.DESC]: makeInnerSorter(DataPointName.MEMBER_JOINS, SortDirection.DESC),
	},
	// This value is negative, so reverse the sorts for it
	[DataPointName.MEMBER_LEAVES]: {
		[SortDirection.ASC]: makeInnerSorter(DataPointName.MEMBER_LEAVES, SortDirection.DESC),
		[SortDirection.DESC]: makeInnerSorter(DataPointName.MEMBER_LEAVES, SortDirection.ASC),
	},
	[DataPointName.SHARE_UPLOADS]: {
		[SortDirection.ASC]: makeInnerSorter(DataPointName.SHARE_UPLOADS, SortDirection.ASC),
		[SortDirection.DESC]: makeInnerSorter(DataPointName.SHARE_UPLOADS, SortDirection.DESC),
	},
	// This value is negative, so reverse the sorts for it
	[DataPointName.SHARE_REMOVES]: {
		[SortDirection.ASC]: makeInnerSorter(DataPointName.SHARE_REMOVES, SortDirection.DESC),
		[SortDirection.DESC]: makeInnerSorter(DataPointName.SHARE_REMOVES, SortDirection.ASC),
	},
	[DataPointName.CREATE_DISCUSSION]: {
		[SortDirection.ASC]: makeInnerSorter(DataPointName.CREATE_DISCUSSION, SortDirection.ASC),
		[SortDirection.DESC]: makeInnerSorter(DataPointName.CREATE_DISCUSSION, SortDirection.DESC),
	},
	[DataPointName.DISCUSSION_POSTS]: {
		[SortDirection.ASC]: makeInnerSorter(DataPointName.DISCUSSION_POSTS, SortDirection.ASC),
		[SortDirection.DESC]: makeInnerSorter(DataPointName.DISCUSSION_POSTS, SortDirection.DESC),
	},
	[DataPointName.READ_DISCUSSION]: {
		[SortDirection.ASC]: makeInnerSorter(DataPointName.READ_DISCUSSION, SortDirection.ASC),
		[SortDirection.DESC]: makeInnerSorter(DataPointName.READ_DISCUSSION, SortDirection.DESC),
	},
	[DataPointName.VIEW]: {
		[SortDirection.ASC]: makeInnerSorter(DataPointName.VIEW, SortDirection.ASC),
		[SortDirection.DESC]: makeInnerSorter(DataPointName.VIEW, SortDirection.DESC),
	},
	[DataPointName.WINDOWED_AVERAGE]: {
		[SortDirection.ASC]: sortByWindowedAverageAsc,
		[SortDirection.DESC]: sortByWindowedAverageDesc,
	},
	[DataPointName.CREATE_EVENT]: {
		[SortDirection.ASC]: makeInnerSorter(DataPointName.CREATE_EVENT, SortDirection.ASC),
		[SortDirection.DESC]: makeInnerSorter(DataPointName.CREATE_EVENT, SortDirection.DESC),
	},
	[DataPointName.FILE_DOWNLOADS]: {
		[SortDirection.ASC]: makeInnerSorter(DataPointName.FILE_DOWNLOADS, SortDirection.ASC),
		[SortDirection.DESC]: makeInnerSorter(DataPointName.FILE_DOWNLOADS, SortDirection.DESC),
	},
};
